import React, { Component } from 'react'
import axios from 'axios'
import { APIURL } from '../../../config/config'
import { Modal, ModalBody } from 'reactstrap'
import Swal from 'sweetalert2'
import CustomerCreditForm from './CustomerCreditForm'
import CustomerForm from '../CustomerForm'
import { printProfileCreditPdf } from '../../../utils/profileCreditPdf'
import { connect } from 'react-redux'

class CustomerCreditProfileForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeId: null,
      profile: null,
      customer: null,
      isLoading: false,

      // sidebar: search customer+profile + prospects (no selection) + add new
      sidebarSearch: '',
      sidebarResults: [],
      sidebarProspects: [],

      // new-customer sub-modal (reuse CustomerForm)
      newCustOpen: false
    }
  }

  componentDidMount() {
    // CoreUI's `.app-body { overflow-x: hidden }` is computed as `hidden auto`,
    // turning it into a scroll container that traps `position: sticky` (the section
    // nav then has no scroll offset to react to). Neutralize it on this page so the
    // sticky nav works; restored on unmount.
    const appBody = document.querySelector('.app-body')
    if (appBody) {
      this._prevAppBodyOverflowX = appBody.style.overflowX
      appBody.style.overflowX = 'visible'
    }
    this.loadFromUrl(this.props.location.search)
  }

  componentWillUnmount() {
    const appBody = document.querySelector('.app-body')
    if (appBody) appBody.style.overflowX = this._prevAppBodyOverflowX || ''
  }

  componentDidUpdate(prevProps) {
    // react to URL change (sidebar selection / list row click / browser back)
    if (prevProps.location.search !== this.props.location.search) {
      this.loadFromUrl(this.props.location.search)
    }
  }

  loadFromUrl(search) {
    const params = new URLSearchParams(search || '')
    const id = params.get('profile_credit_id')
    const customerId = params.get('customer_id')
    if (id) {
      this.openDetail(id)
    } else if (customerId) {
      // draft mode — customer chosen, not yet saved (no profile_credit row yet)
      this.loadDraft(customerId)
    } else {
      // "add new" mode — clear any active profile, load prospect suggestions
      this.setState({ activeId: null, profile: null, customer: null, sidebarSearch: '', sidebarResults: [] })
      this.loadProspects()
    }
  }

  // draft mode: load customer info + blank profile, but do NOT create a row yet
  loadDraft(customerId) {
    axios.get(APIURL + '/customer/' + customerId)
      .then(res => {
        const c = Array.isArray(res.data) ? (res.data[0] || {}) : (res.data || {})
        this.setState({
          activeId: null,
          profile: this.denormalize({ customer_id: customerId }),
          customer: {
            customer_id: customerId,
            customer_name: c.customer_name,
            customer_lastname: c.customer_lastname,
            customer_mobile: c.customer_mobile,
            customer_age: c.customer_age,
            customer_address: c.customer_address,
            customer_gender: c.customer_gender,
            customer_housing: c.customer_housing,
            customer_housing_other: c.customer_housing_other
          },
          sidebarSearch: '', sidebarResults: []
        })
      })
      .catch(() => Swal.fire({ title: 'โหลดข้อมูลลูกค้าไม่สำเร็จ', type: 'error' }))
  }

  // ---------- DETAIL ----------
  openDetail(id) {
    this.setState({ activeId: id, isLoading: true, sidebarSearch: '', sidebarResults: [] })
    axios.get(APIURL + '/profile_credit/' + id)
      .then(res => {
        const p = res.data || {}
        this.setState({
          profile: this.denormalize(p),
          customer: {
            customer_name: p.customer_name, customer_lastname: p.customer_lastname,
            customer_mobile: p.customer_mobile, customer_age: p.customer_age,
            customer_address: p.customer_address, customer_gender: p.customer_gender,
            customer_housing: p.customer_housing, customer_housing_other: p.customer_housing_other,
            customer_id: p.customer_id
          },
          isLoading: false
        })
      })
      .catch(() => this.setState({ isLoading: false }))
  }

  backToList() {
    this.props.history.push('/customer/credit-profile')
  }

  // start a brand-new credit profile (clears the current one, loads prospects)
  goToNew() {
    this.props.history.push('/customer/credit-profile-form')
  }

  // ---------- SIDEBAR ----------
  loadProspects() {
    axios.get(APIURL + '/profile_credit/prospects')
      .then(res => this.setState({ sidebarProspects: res.data || [] }))
      .catch(() => this.setState({ sidebarProspects: [] }))
  }

  searchSidebar(q) {
    this.setState({ sidebarSearch: q })
    if (!q || !q.trim()) { this.setState({ sidebarResults: [] }); return }
    const term = encodeURIComponent(q)
    Promise.all([
      axios.get(APIURL + '/customer/search/' + term).catch(() => ({ data: [] })),
      axios.get(APIURL + '/profile_credit/search/' + term).catch(() => ({ data: [] }))
    ]).then(([custRes, profRes]) => {
      const customers = custRes.data || []
      const profiles = profRes.data || []
      const profileCustIds = new Set(profiles.map(p => p.customer_id))
      const merged = [
        ...profiles.map(p => ({ type: 'profile', id: p.profile_credit_id, customer_id: p.customer_id, customer_name: p.customer_name, customer_lastname: p.customer_lastname, customer_mobile: p.customer_mobile, credit_status: p.credit_status, received_date: p.received_date })),
        ...customers.filter(c => !profileCustIds.has(c.customer_id)).map(c => ({ type: 'customer', id: c.customer_id, customer_id: c.customer_id, customer_name: c.customer_name, customer_lastname: c.customer_lastname, customer_mobile: c.customer_mobile }))
      ]
      this.setState({ sidebarResults: merged })
    })
  }

  selectSidebarItem(item) {
    if (item.type === 'profile') {
      this.props.history.push('/customer/credit-profile-form?profile_credit_id=' + item.id)
    } else {
      // customer without profile → draft mode (no row created until save)
      this.props.history.push('/customer/credit-profile-form?customer_id=' + item.customer_id)
    }
  }

  onNewCustomerCreated(customer_id) {
    this.setState({ newCustOpen: false })
    if (customer_id) {
      this.props.history.push('/customer/credit-profile-form?customer_id=' + customer_id)
    }
  }

  // ---------- SAVE / DELETE ----------
  save() {
    const { profile, customer, activeId } = this.state
    if (!customer || !customer.customer_id) {
      Swal.fire({ title: 'กรุณาเลือกลูกค้าก่อน', type: 'warning' })
      return
    }
    const payload = this.normalize(profile)
    payload.customer_id = customer.customer_id
    payload.customer_gender = customer.customer_gender
    payload.customer_housing = customer.customer_housing
    payload.customer_housing_other = customer.customer_housing_other

    if (activeId) {
      // update existing
      axios.put(APIURL + '/profile_credit/' + activeId, payload)
        .then(() => Swal.fire({ title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false }))
        .catch(() => Swal.fire({ title: 'บันทึกไม่สำเร็จ', type: 'error' }))
    } else {
      // create new — only now does a row get created (was previously created on select)
      axios.post(APIURL + '/profile_credit/', payload)
        .then(res => {
          const newId = (res.data && res.data.profile_credit_id) || (res.data && res.data.customer_credit_id)
          Swal.fire({ title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false })
          if (newId) this.props.history.replace('/customer/credit-profile-form?profile_credit_id=' + newId)
        })
        .catch(() => Swal.fire({ title: 'บันทึกไม่สำเร็จ', type: 'error' }))
    }
  }

  remove() {
    Swal.fire({ title: 'ลบ credit profile นี้?', type: 'warning', showCancelButton: true })
      .then(r => {
        if (!r.value) return
        axios.delete(APIURL + '/profile_credit/' + this.state.activeId)
          .then(() => { Swal.fire({ title: 'ลบแล้ว', timer: 1000, showConfirmButton: false }); this.backToList() })
      })
  }

  // ---------- CHANGE HANDLERS (form) ----------
  onChange(path, value) {
    if (path.startsWith('profile.')) {
      const key = path.slice('profile.'.length)
      this.setState(s => ({ profile: { ...s.profile, [key]: value } }))
    } else if (path.startsWith('customer.')) {
      const key = path.slice('customer.'.length)
      this.setState(s => ({ customer: { ...s.customer, [key]: value } }))
    }
  }
  onArrayChange(jsonKey, i, field, value) {
    this.setState(s => {
      const arr = [...(s.profile[jsonKey] || [])]
      arr[i] = { ...arr[i], [field]: value }
      return { profile: { ...s.profile, [jsonKey]: arr } }
    })
  }
  onArrayAdd(jsonKey, empty) {
    this.setState(s => ({ profile: { ...s.profile, [jsonKey]: [...(s.profile[jsonKey] || []), empty] } }))
  }
  onArrayRemove(jsonKey, i) {
    this.setState(s => {
      const arr = (s.profile[jsonKey] || []).filter((_, idx) => idx !== i)
      return { profile: { ...s.profile, [jsonKey]: arr } }
    })
  }
  onBankChange(code, field, value) {
    this.setState(s => {
      const banks = { ...(s.profile.banks_json || {}) }
      banks[code] = { ...(banks[code] || { on: false, note: '' }), [field]: value }
      return { profile: { ...s.profile, banks_json: banks } }
    })
  }

  denormalize(p) {
    const padArr = (arr, n, empty) => {
      const out = (arr || []).slice(0, n)
      while (out.length < n) out.push({ ...empty })
      return out
    }
    return {
      ...p,
      obligations_json: padArr(p.obligations_json, 4, { name: '', installment: '', paidSoFar: '' }),
      history_json: padArr(p.history_json, 3, { period: '', finance: '', where: '' }),
      banks_json: p.banks_json || {},
      followups_json: padArr(p.followups_json, 6, { date: '', note: '' })
    }
  }
  normalize(p) {
    const out = {}
    const single = ['received_date','channel','vehicle_model_interest','desired_installment','down_budget',
      'employer','employed_income_amt','work_years','has_slip','has_certificate','income_cash',
      'sso_status','sso_months','sso_other','bank_account','has_license','license_note',
      'business','business_years','business_docs','business_income_amt','has_lease_contract','statement',
      'cred_home','cred_motorcycle','cred_car','cred_credit_card','cred_personal_loan','cred_refinance',
      'cred_other','cred_other_note','credit_status','credit_status_other','obligations_total','banks_other']
    single.forEach(c => { out[c] = p[c] })
    out.obligations_json = p.obligations_json || []
    out.history_json = p.history_json || []
    out.banks_json = p.banks_json || {}
    out.followups_json = p.followups_json || []
    // auto-calc total from the installment column
    out.obligations_total = (p.obligations_json || []).reduce((s, o) => s + (parseFloat(o.installment) || 0), 0)
    return out
  }

  // ---------- RENDER ----------
  renderSidebar() {
    const { sidebarSearch, sidebarResults, sidebarProspects, activeId, customer } = this.state

    let body
    if (sidebarSearch && sidebarSearch.trim()) {
      body = sidebarResults.length === 0
        ? <div className="text-muted small p-2">ไม่พบรายการ</div>
        : sidebarResults.map(item => {
            const isActive = (item.type === 'profile' && item.id === activeId) || (customer && item.customer_id === customer.customer_id)
            return (
            <div key={item.type + '-' + item.id}
                 className={'p-2 mb-1 rounded' + (isActive ? ' bg-primary text-white' : '')}
                 style={{ cursor: 'pointer', border: isActive ? '1px solid #0d5cb8' : '1px solid #eee' }}
                 onClick={() => this.selectSidebarItem(item)}>
              <div><strong>{item.customer_name} {item.customer_lastname}</strong></div>
              <small>{item.customer_mobile || 'ไม่มีเบอร์'}</small>{' '}
              {item.type === 'profile'
                ? <span className={'badge ' + (isActive ? 'badge-light' : 'badge-success')}>มี profile</span>
                : <span className={'badge ' + (isActive ? 'badge-light' : 'badge-secondary')}>ยังไม่มี profile</span>}
            </div>
            )
          })
    } else if (activeId && customer) {
      body = (
        <div className="p-2 mb-1 rounded bg-primary text-white" style={{ border: '1px solid #0d5cb8' }}>
          <div><strong>{customer.customer_name} {customer.customer_lastname}</strong></div>
          <small>{customer.customer_mobile || 'ไม่มีเบอร์'}</small>
        </div>
      )
    } else if (sidebarProspects.length > 0) {
      body = sidebarProspects.map(c => {
        const isActive = customer && c.customer_id === customer.customer_id
        return (
        <div key={'cust-' + c.customer_id}
             className={'p-2 mb-1 rounded' + (isActive ? ' bg-primary text-white' : '')}
             style={{ cursor: 'pointer', border: isActive ? '1px solid #0d5cb8' : '1px solid #eee' }}
             onClick={() => this.selectSidebarItem({ type: 'customer', customer_id: c.customer_id })}>
          <div><strong>{c.customer_name} {c.customer_lastname}</strong></div>
          <small>{c.customer_mobile || 'ไม่มีเบอร์'}</small>{' '}
          <span className={'badge ' + (isActive ? 'badge-light' : 'badge-secondary')}>ยังไม่มี profile</span>
        </div>
        )
      })
    } else {
      body = <div className="text-muted small p-2">ค้นหา หรือ กด “เพิ่มลูกค้าใหม่”</div>
    }

    const isNew = !activeId
    return (
      <div className="card">
        <div className="card-block">
          {isNew && (
            <React.Fragment>
              <input className="form-control mb-2" placeholder="ค้นหา ชื่อ / เบอร์" value={sidebarSearch}
                onChange={e => this.searchSidebar(e.target.value)} />
              <button className="btn btn-success btn-block mb-3" onClick={() => this.setState({ newCustOpen: true })}>
                <i className="icon-user-follow"></i> เพิ่มลูกค้าใหม่
              </button>
            </React.Fragment>
          )}
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>{body}</div>
        </div>
      </div>
    )
  }

  render() {
    const { customer } = this.state
    return (
      <div className="animated fadeIn">
        <div className="col-md-12 mb-4">
          <div className="mb-2">
            <button className="btn btn-secondary btn-sm" onClick={() => this.backToList()}>
              <i className="icon-arrow-left"></i> กลับสู่รายการ
            </button>{' '}
            <button className="btn btn-primary btn-sm" onClick={() => this.goToNew()}>
              <i className="icon-user-follow"></i> เพิ่มเครดิตลูกค้า
            </button>
            {this.state.activeId && this.state.profile && (
              <React.Fragment>
                {' '}
                <button className="btn btn-info btn-sm" onClick={() => printProfileCreditPdf(this.state.profile, this.state.customer)}>
                  <i className="icon-printer"></i> พิมพ์
                </button>
              </React.Fragment>
            )}
          </div>
          <div className="row">
            {/* LEFT: sidebar */}
            <div className="col-md-3">
              {this.renderSidebar()}
            </div>
            {/* RIGHT: form */}
            <div className="col-md-9">
              <CustomerCreditForm
                profile={this.state.profile}
                customer={customer}
                canDelete={!!this.state.activeId}
                onChange={(p, v) => this.onChange(p, v)}
                onArrayChange={(k, i, f, v) => this.onArrayChange(k, i, f, v)}
                onArrayAdd={(k, e) => this.onArrayAdd(k, e)}
                onArrayRemove={(k, i) => this.onArrayRemove(k, i)}
                onBankChange={(code, f, v) => this.onBankChange(code, f, v)}
                onSave={() => this.save()}
                onDelete={() => this.remove()}
              />
            </div>
          </div>
        </div>

        <Modal isOpen={this.state.newCustOpen} toggle={() => this.setState({ newCustOpen: false })} className="modal-lg">
          <ModalBody>
            <CustomerForm onToggle={(customer_id) => this.onNewCustomerCreated(customer_id)} />
          </ModalBody>
        </Modal>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return { user: state.auth && state.auth.user ? JSON.stringify(state.auth.user) : '{}' }
}
export default connect(mapStateToProps)(CustomerCreditProfileForm)
